import React, { useEffect, useState } from 'react'
import {
  BooleanInput,
  SimpleForm,
  TextInput,
  useEditContext,
  useGetList,
  required,
} from 'react-admin'
import { useFormContext, useWatch } from 'react-hook-form'
import { Grid } from '@mui/material'
import PermissionTable from '../../components/domains/roles-permission/PermissionTable'
import { EditBase } from '../../components/base'
import { COMMON_FIELDS, ROLES_FIELDS } from '../../providers/localization/constant'
import { FormBottomToolbarWithPermission } from '../../components/form'
import { extractPermisionSubmenu, generateValuePermissions } from '../../utils/user'
import isEmpty from 'lodash/isEmpty'
import { RolePermission } from './type'
import { AllPermissionBooleanInput } from '../../components/domains/roles-permission'

const RESOURCE_PREFIX = 'resources.roles.edit'

const RolesPermissionsEditFormBody = (): JSX.Element => {
  const { record, isFetching } = useEditContext()
  const { getValues, setValue } = useFormContext()
  const [rolesPermission, setRolesPermission] = useState<RolePermission[] | []>([])
  const [extractedPermissions, setExtractedPermissions] = useState<RolePermission[] | []>([])
  const [isRegistering, setisRegistering] = useState(true)
  const { data: permissionKeys } = useGetList('permissions', {
    sort: { field: 'key', order: 'ASC' },
  })

  /**
   * Effect #1
   * - set extract permisison state from record.permission
   * */
  useEffect(() => {
    if (record?.id && !isEmpty(record?.permissions)) {
      setExtractedPermissions(() => extractPermisionSubmenu(record.permissions))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(record)])

  /**
   * Effect #2
   * - set the permissions value to react hook form
   * */
  useEffect(() => {
    if (!isEmpty(permissionKeys) && !isEmpty(extractedPermissions) && !isFetching) {
      // Iterate over each permission to register each permission of each feature to FormContext
      const registerPermissions = generateValuePermissions(permissionKeys, extractedPermissions)
      setValue('permissions', registerPermissions)
      setisRegistering(false)
    }
  }, [permissionKeys, isFetching, extractedPermissions, setValue])

  /**
   * Effect #3
   * - set rolesPermission state value from getValues (form)
   * */
  useEffect(() => {
    if (!isRegistering) setRolesPermission(() => getValues()?.permissions || [])
  }, [getValues, isRegistering])

  const watchPermissions = useWatch({ name: 'permissions' })
  useEffect(() => {
    if (!isEmpty(rolesPermission) && !isFetching) {
      const defaultAllPermission = []
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .concat(...rolesPermission.map(({ key, ...item }) => Object.values(item)))
        .every((element) => element)
      setValue('all_permission', defaultAllPermission)
    }
  }, [isFetching, rolesPermission, setValue, watchPermissions])

  if (isFetching || isRegistering || isEmpty(permissionKeys) || isEmpty(rolesPermission))
    return null

  return (
    <>
      <Grid container spacing={2} alignItems='center'>
        <Grid item xs={12} sm={4}>
          <TextInput
            source='name'
            label={`${ROLES_FIELDS}.name`}
            validate={[required()]}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <BooleanInput source='is_active' label={`${COMMON_FIELDS}.status.label`} />
        </Grid>
        <Grid item xs={12} sm={3}>
          <AllPermissionBooleanInput />
        </Grid>
      </Grid>
      <PermissionTable rolePermissionList={rolesPermission} permissionKeys={permissionKeys} />
    </>
  )
}

const RolesPermissionsEdit = (): JSX.Element => {
  return (
    <EditBase
      title={`${RESOURCE_PREFIX}.title`}
      redirect='list'
      listAction={{
        hasShow: false,
        hasBackButton: true,
      }}
    >
      <SimpleForm toolbar={<FormBottomToolbarWithPermission hasConfirm={true} />}>
        <RolesPermissionsEditFormBody />
      </SimpleForm>
    </EditBase>
  )
}

export default RolesPermissionsEdit
